import { apiClient } from "../../lib/apiClient";
import { bytesUploader } from "../../lib/upload";
import { SIGNING_WINDOW_DAYS } from "../projects/api";
import type {
  GiftDirection,
  Player,
  PlayerDeletionPreview,
  PlayerDeletionResult,
  PlayerGiftsResponse,
  PlayerGuild,
  PlayerIdentity,
  PlayerLeaderboards,
  PlayerReferralsResponse,
  PlayersListResponse,
  UpdatePlayerInput,
  PlayerCloudSave,
  PlayerCloudSaveWritten,
} from "./types";

export interface ListPlayersParams {
  q?: string;
  page?: number;
  limit?: number;
  /**
   * Include the cold-start simulation's players. Omitted or false, the list —
   * its rows, its total and its search alike — is the living players only,
   * which is the same set the Players card on Overview counts.
   */
  bots?: boolean;
  /**
   * Only the players connected right now. A third independent narrowing of the
   * same list, alongside `q` and `bots` — never a replacement for either.
   */
  online?: boolean;
}

/** Fetch a page of players for a project. */
export async function listPlayers(
  projectId: string,
  params: ListPlayersParams = {},
): Promise<PlayersListResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.page) qs.set("page", String(params.page));
  if (params.limit) qs.set("limit", String(params.limit));
  // Written only when on, and as the one value the server reads. Off is the
  // default on both sides, so an absent parameter says it exactly.
  if (params.bots) qs.set("bots", "1");
  // Same rule as `bots`: written only when on, and only ever as "1".
  if (params.online) qs.set("online", "1");
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const r = await apiClient.get<PlayersListResponse>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/players${suffix}`,
  );
  return r.data;
}

/** Fetch one player's full record. */
export async function getPlayer(
  projectId: string,
  playerId: string,
): Promise<Player> {
  const r = await apiClient.get<Player>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/players/${encodeURIComponent(playerId)}`,
  );
  return r.data;
}

/**
 * Fetch the player's linked identities. Read-only. The server orders them
 * oldest first; the Linked-accounts card re-sorts for display (it sinks the
 * anonymous device identity to the bottom).
 */
export async function getPlayerIdentities(
  projectId: string,
  playerId: string,
): Promise<{ identities: PlayerIdentity[] }> {
  const r = await apiClient.get<{ identities: PlayerIdentity[] }>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/players/${encodeURIComponent(playerId)}/identities`,
  );
  return r.data;
}

/**
 * Fetch one direction of the player's gifts — the last 20, newest first, plus
 * the four-number summary that rides on both answers. Read-only: there is no
 * cancelling or returning a gift, in this API or in the game.
 *
 * `direction` is a literal union, so it goes straight into the query string
 * rather than through URLSearchParams: there is nothing here to escape, and
 * the two values it can take are in the type.
 */
export async function getPlayerGifts(
  projectId: string,
  playerId: string,
  direction: GiftDirection,
): Promise<PlayerGiftsResponse> {
  const r = await apiClient.get<PlayerGiftsResponse>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/players/${encodeURIComponent(playerId)}/gifts?direction=${direction}`,
  );
  return r.data;
}

/**
 * Who brought this player in, whom this player brought in, and the trigger all
 * of it was counted under. Read-only: an invite is a thing that happened, and
 * the API has nothing to undo or re-award it with.
 *
 * One request for both sides — the two halves answer the same support question
 * ("was he paid for his invites?") and splitting them would only mean two
 * spinners in one card.
 */
export async function getPlayerReferrals(
  projectId: string,
  playerId: string,
): Promise<PlayerReferralsResponse> {
  const r = await apiClient.get<PlayerReferralsResponse>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/players/${encodeURIComponent(playerId)}/referrals`,
  );
  return r.data;
}

/**
 * The guild the player is in, with its roster figures — or null when the
 * player is in none, which is an answer and not a missing record.
 */
export async function getPlayerGuild(
  projectId: string,
  playerId: string,
): Promise<PlayerGuild | null> {
  const r = await apiClient.get<PlayerGuild | null>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/players/${encodeURIComponent(playerId)}/guild`,
  );
  return r.data;
}

/**
 * Every cycle running right now that the player has a score on, by themselves
 * and through their guild, with the place and the reward each one stands at.
 */
export async function getPlayerLeaderboards(
  projectId: string,
  playerId: string,
): Promise<PlayerLeaderboards> {
  const r = await apiClient.get<PlayerLeaderboards>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/players/${encodeURIComponent(playerId)}/leaderboards`,
  );
  return r.data;
}

/** PATCH a player. Last-write-wins — no If-Match etag. Returns the updated record. */
export async function updatePlayer(
  projectId: string,
  playerId: string,
  input: UpdatePlayerInput,
): Promise<Player> {
  const r = await apiClient.patch<Player>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/players/${encodeURIComponent(playerId)}`,
    input,
  );
  return r.data;
}

/**
 * Read what deleting this player would affect. Called when the confirmation
 * dialog opens, not when the page loads: a player card is opened to edit a
 * player, not to delete one.
 */
export async function getPlayerDeletionPreview(
  projectId: string,
  playerId: string,
): Promise<PlayerDeletionPreview> {
  const r = await apiClient.get<PlayerDeletionPreview>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/players/${encodeURIComponent(playerId)}/deletion-preview`,
  );
  return r.data;
}

/**
 * Delete a player for good; 404 once they are already gone.
 *
 * The response carries the guild verdict as it ACTUALLY fell — not as the
 * preflight predicted it. The two can disagree: the preview is read while the
 * operator is still typing the player's name, and in those seconds the named
 * heir can leave, the last living member can walk out and turn a transfer into
 * a disband, or somebody can join and save a guild the preview had written
 * off. The toast is built from this.
 */
export async function deletePlayer(
  projectId: string,
  playerId: string,
): Promise<PlayerDeletionResult> {
  const r = await apiClient.delete<PlayerDeletionResult>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/players/${encodeURIComponent(playerId)}`,
  );
  return r.data;
}

/**
 * One player's signature failures over the last seven days, and the project's
 * signing mode beside them.
 *
 * The mode travels with the count because zero means two different things: in
 * `off` nothing is verified and nothing is counted, so a zero would read as
 * "this player is clean" when the truth is "nobody looked". `available` tells
 * a real zero from a counter store that did not answer — the card shows all
 * three differently, and shows the cell in every one of them.
 */
export interface PlayerSignatureFailures {
  window_days: number;
  available: boolean;
  count: number;
  mode: string;
}

/**
 * Read the player's signature-failure counter. A route of its own rather than a
 * field on the player record: a silent counter store then costs the card one
 * cell instead of the whole page.
 */
export async function getPlayerSignatureFailures(
  projectId: string,
  playerId: string,
): Promise<PlayerSignatureFailures> {
  const r = await apiClient.get<PlayerSignatureFailures>(
    // Same window the project's own counters are read over — the cell's label
    // and the Config page's headline have to mean the same seven days.
    `/api/admin/projects/${encodeURIComponent(projectId)}/players/${encodeURIComponent(playerId)}/signature-failures?days=${SIGNING_WINDOW_DAYS}`,
  );
  return r.data;
}

function cloudSaveBase(projectId: string, playerId: string): string {
  return `/api/admin/projects/${encodeURIComponent(projectId)}/players/${encodeURIComponent(playerId)}/cloud-save`;
}

/** The slot's facts. Answers even on a server with no bucket. */
export async function getPlayerCloudSave(
  projectId: string,
  playerId: string,
): Promise<PlayerCloudSave> {
  const r = await apiClient.get<PlayerCloudSave>(cloudSaveBase(projectId, playerId));
  return r.data;
}

/** Where the bytes are, for the file downloader. */
export function playerCloudSaveContentPath(
  projectId: string,
  playerId: string,
): string {
  return `${cloudSaveBase(projectId, playerId)}/content`;
}

/**
 * PUT a file in the slot's place. `expected` is what the card showed — the
 * save's sha256, or `null` for an empty slot — and travels as the request's
 * precondition (`If-Match: "<sha256>"`, or `If-None-Match: *`). The server
 * refuses to write without one and answers 409 when the slot no longer
 * matches, so an operator never overwrites a save the game wrote meanwhile
 * unknowingly — a save that appeared in an empty slot included.
 */
export async function uploadPlayerCloudSave(
  projectId: string,
  playerId: string,
  bytes: ArrayBuffer,
  expected: string | null,
  signal?: AbortSignal,
): Promise<PlayerCloudSaveWritten> {
  return bytesUploader.put<PlayerCloudSaveWritten>(
    playerCloudSaveContentPath(projectId, playerId),
    bytes,
    expected === null ? { "If-None-Match": "*" } : { "If-Match": `"${expected}"` },
    signal,
  );
}

/** Empty the slot. 204 whether or not there was a save. */
export async function deletePlayerCloudSave(
  projectId: string,
  playerId: string,
): Promise<void> {
  await apiClient.delete(cloudSaveBase(projectId, playerId));
}
